import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { supabase } from '../../lib/supabase';
import { getSignedInDestination } from '../../lib/sessionNavigation';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    let active = true;
    let requestVersion = 0;
    let authorizedUserId: string | null = null;
    let authTimer: ReturnType<typeof setTimeout> | undefined;

    async function checkAuth() {
      const version = ++requestVersion;
      if (!authorizedUserId) {
        setIsAuthenticated(false);
        setIsLoading(true);
      }
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!active || version !== requestVersion) return;

        if (!session) {
          navigate('/login', { replace: true });
          return;
        }

        const destination = await getSignedInDestination(supabase, session.user.id);
        if (!active || version !== requestVersion) return;
        if (destination !== '/kovadash') {
          navigate(destination ?? '/login', { replace: true });
          return;
        }

        authorizedUserId = session.user.id;
        setIsAuthenticated(true);
        setIsLoading(false);
      } catch (error) {
        if (!active || version !== requestVersion) return;
        console.error('Auth check error:', error);
        navigate('/login', { replace: true });
      }
    }

    void checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      // checkAuth above already handles the initial session. Starting it again
      // invalidates the first result and repeats the profile query.
      if (_event === 'INITIAL_SESSION') return;
      if (!session) {
        ++requestVersion;
        authorizedUserId = null;
        setIsAuthenticated(false);
        navigate('/login', { replace: true });
      } else {
        // Refreshes and repeated sign-in notifications for the same user must
        // not unmount an editor and discard its local draft state.
        if (session.user.id === authorizedUserId &&
            ['TOKEN_REFRESHED', 'SIGNED_IN', 'INITIAL_SESSION'].includes(_event)) return;
        ++requestVersion;
        if (session.user.id !== authorizedUserId) {
          authorizedUserId = null;
          setIsAuthenticated(false);
          setIsLoading(true);
        }
        // Run outside the auth callback so Supabase auth calls do not contend for its lock.
        clearTimeout(authTimer);
        authTimer = setTimeout(() => { void checkAuth(); }, 0);
      }
    });

    return () => {
      active = false;
      clearTimeout(authTimer);
      subscription.unsubscribe();
    };
  }, [navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[var(--theme-background)] flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={32} strokeWidth={1.5} className="text-[var(--theme-primary)]/30 animate-spin mx-auto mb-4" />
          <p className="text-[var(--theme-text)]/40 uppercase tracking-[0.2em]" style={{ fontSize: '10px' }}>
            Verifying Access...
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return <>{children}</>;
}
